import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DataComponent from '../../../DataComponent/DataComponent';
import { isMobile } from '../../../../utils/DeviceDetect';
import CustomModal from '../../../Modal';
import CustomTooltip from '../../../Tooltip';
import { Link } from '../../../RouterCompatibility';
import { useTranslation } from '../../../ChildrenContext';
import runtimeConfig from '../../../../config/runtimeConfig';
import UserStore from '../../../../stores/UserStore';

const images = {
    eye: require('shared_img/icons/icon-eye.svg'),
    postbackAdd: require('shared_img/icons/postback-add-icon.svg')
};

const generateSessionId = (len, charSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
    let randomString = '';
    for (let i = 0; i < len; i++) {
        const randomPoz = Math.floor(Math.random() * charSet.length);
        randomString += charSet.substring(randomPoz, randomPoz + 1);
    }
    return randomString;
};

const filterList = (list = [], headerNames = []) => {
    if (!headerNames || !headerNames.length) {
        return list;
    }

    const visibleHeaders = headerNames.filter(header => header.visible);
    return list.reduce((acc, conversion) => {
        const filtered = {};
        let hasVisibleField = false;

        Object.keys(conversion).forEach(field => {
            const header = visibleHeaders.find(({ keyTitle }) => keyTitle === field);
            if (header) {
                filtered[field] = conversion[field];
                hasVisibleField = true;
            }
        });

        if (hasVisibleField) {
            acc.push(filtered);
        }
        return acc;
    }, []);
};

const getUserTimezone = () => UserStore.getValidTimeZone();

const getDefaultDateRange = () => {
    const now = moment().tz(getUserTimezone());

    return {
        startDate: now.clone().subtract(6, 'days').startOf('day'),
        endDate: now.clone().endOf('day')
    };
};

const ensureDefaultDateRange = (state) => {
    const hasCustomPeriod = state && (state.createStartDate || state.createEndDate);
    if (hasCustomPeriod) {
        return state;
    }

    const { startDate, endDate } = getDefaultDateRange();

    return {
        ...state,
        createStartDate: startDate,
        createEndDate: endDate
    };
};

const ConversionList = ({
    list,
    totalPages,
    totalItems,
    page,
    loading,
    headerNames,
    statuses,
    actionTypes,
    actionNames,
    getConversions,
    getTotalRows,
    onAddPostback
}) => {
    const t = useTranslation();
    const staticNameTable = t?.dataTable || {};
    const staticHeaderNames = staticNameTable.headers || {};
    const localizedHeaders = useMemo(() => {
        return (headerNames || []).map((item) => {
            if (!item || typeof item !== 'object') {
                return item;
            }
            const localizedTitle = staticHeaderNames[item.keyTitle] || item.title || item.keyTitle;
            if (item.title === localizedTitle) {
                return item;
            }
            return {
                ...item,
                title: localizedTitle
            };
        });
    }, [headerNames, staticHeaderNames]);
    const staticNamesAction = t?.actions || {};
    const staticConversionPage = t?.conversionPage || {};
    const sessionId = useMemo(() => generateSessionId(32), []);
    const isMobileDevice = useMemo(() => Boolean(isMobile && typeof isMobile.any === 'function' && isMobile.any()), []);
    const headerTitleByKey = useMemo(() => {
        return (localizedHeaders || []).reduce((acc, item) => {
            if (item && item.keyTitle) {
                acc[item.keyTitle] = item.title;
            }
            return acc;
        }, {});
    }, [localizedHeaders]);
    const getHeaderTitle = useCallback((key, fallback) => headerTitleByKey[key] || fallback, [headerTitleByKey]);
    const prepareState = useCallback((state) => {
        const withDefaults = ensureDefaultDateRange({ ...(state || {}) });
        return {
            ...withDefaults,
            page: withDefaults.page || runtimeConfig.initialPage,
            sessionId
        };
    }, [sessionId]);

    const [displayList, setDisplayList] = useState(() => filterList(list, localizedHeaders));
    const [tableLoading, setTableLoading] = useState(loading);
    const [addPostbackModalIsVisible, setAddPostbackModalIsVisible] = useState(false);
    const [conversionIdForPostback, setConversionIdForPostback] = useState(null);
    const [conversionStatusForPostback, setConversionStatusForPostback] = useState(null);

    useEffect(() => {
        const nextList = filterList(list, localizedHeaders);
        setDisplayList(nextList);
        setTableLoading(!!loading);
    }, [list, localizedHeaders, loading]);

    const handleDataTableAction = useCallback((state) => {
        getConversions(prepareState(state));
    }, [getConversions, prepareState]);

    const handleDataTableFilter = useCallback((state) => {
        getTotalRows(prepareState(state));
    }, [getTotalRows, prepareState]);

    const showAddPostbackModal = useCallback((id, status) => {
        setConversionIdForPostback(id);
        setConversionStatusForPostback(status);
        setAddPostbackModalIsVisible(true);
    }, []);

    const closeAddPostbackModal = useCallback(() => {
        setAddPostbackModalIsVisible(false);
    }, []);

    const handleAddPostback = useCallback(() => {
        onAddPostback({
            conversionIdForPostback,
            conversionStatusForPostback
        });
        setAddPostbackModalIsVisible(false);
    }, [onAddPostback, conversionIdForPostback, conversionStatusForPostback]);

    const listWithActions = useMemo(() => {
        return (displayList || []).map((item, index) => {
            const actions = item.actions;
            if (!actions) {
                return item;
            }
            const tooltipId = actions?.i ?? actions?.id ?? index;
            return {
                ...item,
                actions: (
                    <div className="row-controls">
                        <CustomTooltip id={`viewConverionTooltip-${actions.id}`} tooltip={staticNamesAction.view} placement="top">
                            <Link className="control-item view-button" to={actions.path}>
                                <img src={images.eye} alt="" />
                                <span className="link-title">{staticNamesAction.view}</span>
                            </Link>
                        </CustomTooltip>
                        <CustomTooltip id={`postbackAddTooltip-${actions.id}`} tooltip={staticNamesAction.postbackAdd} placement="top">
                            <button
                                type="button"
                                className="control-item postback-add-button"
                                onClick={() => showAddPostbackModal(actions.id, actions.status)}
                            >
                                <img src={images.postbackAdd} alt="" />
                                <span className="link-title">{staticNamesAction.postbackAdd}</span>
                            </button>
                        </CustomTooltip>
                    </div>
                )
            };
        });
    }, [displayList, staticNamesAction, showAddPostbackModal]);

    return (
        <div className={`history-postback__table ${isMobileDevice ? 'mobile-view' : ''}`}>
            <CustomModal visible={addPostbackModalIsVisible} onClose={closeAddPostbackModal} onConfirm={handleAddPostback}>
                <p>
                    {staticConversionPage.addPostbackNoty} «{conversionIdForPostback}» ({conversionStatusForPostback})?
                </p>
            </CustomModal>
            <DataComponent
                onAction={handleDataTableAction}
                onActionFilter={handleDataTableFilter}
                totalPages={totalPages}
                totalRows={totalItems}
                headerNames={localizedHeaders}
                headerNamesArray={false}
                autoTile={true}
                data={listWithActions}
                loading={tableLoading}
                sessionId={sessionId}
                isQuery={true}
                commonFilterControls={true}
                settings={true}
                settingKey={'conversions'}
                tableBodyTitle={staticConversionPage.conversions}
                className="simple-table green-hover"
                gray={true}
                fields={[
                    {
                        name: 'datepicker',
                        key: 'create',
                        label: getHeaderTitle('createdAt', staticNameTable.headers?.createdAt),
                        dropdownLabel: staticNameTable.filter,
                        disabled: true,
                        // withTimezone: true
                    },
                    {
                        name: 'timezone',
                        standalone: true, // e.g. not bound to a datepicker
                        key: 'timezone',
                        label: t?.timezone?.title,
                        dropdownLabel: staticNameTable.filter,
                        disabled: true
                    },
                    {
                        name: 'switch',
                        key: 'onlyForTrafficInPeriod',
                        label: staticNameTable.onlyForTrafficInPeriod || 'Only for traffic of the selected period',
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'datepicker',
                        key: 'updated',
                        label: getHeaderTitle('updatedAt', staticNameTable.headers?.updatedAt),
                        dropdownLabel: staticNameTable.filter,
                        // withTimezone: true
                    },
                    {
                        name: 'datepicker',
                        key: 'trackCreated',
                        label: getHeaderTitle('createdAtTrack', staticNameTable.headers?.createdAtTrack),
                        dropdownLabel: staticNameTable.filter,
                        // withTimezone: true
                    },
                    {
                        name: 'dynamic',
                        key: 'offer',
                        segment: 'Offer',
                        label: staticNameTable.headers?.offerTitle,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'goalTree',
                        key: 'goals',
                        nesting: 'goals',
                        label: staticNameTable.headers?.goalTitle,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'goalTree',
                        key: 'goalActions',
                        label: staticNameTable.headers?.goalActions,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'statuses',
                        data: statuses,
                        placeholder: staticNameTable.tips?.byStatus,
                        label: staticNameTable.headers?.status,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'actionTypes',
                        data: actionTypes,
                        placeholder: staticNameTable.tips?.byActionType,
                        label: staticNameTable.headers?.typeAction,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'actionNames',
                        data: actionNames,
                        placeholder: staticNameTable.tips?.byActionName,
                        label: staticNameTable.headers?.nameAction,
                        dropdownLabel: staticNameTable.filter
                    },
                    { name: 'custom', key: 'trackIds', numericOnly: true, label: staticNameTable.trackIdFilter || 'Track ID', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'conversionIds', numericOnly: true, label: staticNameTable.conversionIdFilter || 'Conversion ID', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'advCids', label: staticNameTable.headers?.advCid, dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'utmMedium', label: 'UtmMedium', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'utmSource', label: 'UtmSource', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'utmCampaign', label: 'UtmCampaign', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'utmTerm', label: 'UtmTerm', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'utmContent', label: 'UtmContent', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'param1', label: 'Param1', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'param2', label: 'Param2', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'param3', label: 'Param3', dropdownLabel: staticNameTable.filter },
                    { name: 'custom', key: 'param4', label: 'Param4', dropdownLabel: staticNameTable.filter }
                ]}
            />
        </div>
    );
};

ConversionList.propTypes = {
    list: PropTypes.array,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    page: PropTypes.number,
    loading: PropTypes.bool,
    headerNames: PropTypes.array,
    statuses: PropTypes.array,
    actionTypes: PropTypes.array,
    actionNames: PropTypes.array,
    getConversions: PropTypes.func,
    getTotalRows: PropTypes.func,
    onAddPostback: PropTypes.func
};

ConversionList.defaultProps = {
    list: [],
    totalPages: 0,
    totalItems: 0,
    page: 1,
    loading: false,
    headerNames: [],
    statuses: [],
    actionTypes: [],
    actionNames: [],
    getConversions: () => {},
    getTotalRows: () => {},
    onAddPostback: () => {}
};

export default ConversionList;
